import {type AccountInfo, InteractionRequiredAuthError, PublicClientApplication} from "@azure/msal-browser"
import {clientId, tenantId} from "./config"

const storageScopes = ["https://storage.azure.com/user_impersonation"]

export interface Session {
  account: AccountInfo
  getStorageToken: () => Promise<string>
  signOut: () => Promise<void>
}

/**
 * Returns the signed-in session, or redirects to the Entra ID sign-in and never resolves.
 */
export async function signIn(): Promise<Session> {
  const app = new PublicClientApplication({
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri: `${location.origin}/`,
    },
    cache: {cacheLocation: "sessionStorage"},
  })
  await app.initialize()

  const redirect = await app.handleRedirectPromise()
  const account = redirect?.account ?? app.getAllAccounts()[0]
  if (!account) {
    await app.loginRedirect({scopes: storageScopes})
    return new Promise(() => {})
  }
  app.setActiveAccount(account)

  return {
    account,
    async getStorageToken() {
      try {
        return (await app.acquireTokenSilent({scopes: storageScopes, account})).accessToken
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await app.acquireTokenRedirect({scopes: storageScopes, account})
          return new Promise(() => {})
        }
        throw error
      }
    },
    async signOut() {
      await app.logoutRedirect({account, postLogoutRedirectUri: `${location.origin}/`})
    },
  }
}
